import React from "react";
import { Box } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { LoadingButton } from "./LoadingButton.js";

/**
 * Generic floating button that appears from the bottom with a backdrop
 * Features Partiful-style concentric outline rings and smooth animations
 *
 * @param {boolean} open - Controls button visibility
 * @param {boolean} loading - Shows loading state
 * @param {function} onClick - Click handler
 * @param {string} id - Optional custom ID for the button
 * @param {string} dataTestId - Optional test ID
 * @param {React.ReactNode} children - Button text/content
 */
const FloatingButton = ({
  open,
  loading,
  onClick,
  id = "floating-button",
  dataTestId = "floating-button",
  children
}) => {
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop/Scrim for emphasis */}
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              top: 0,
              zIndex: 1299,
              pointerEvents: "none",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.07) 30%, transparent 60%)",
            }}
          />
          {/* Button Container with Partiful-style outline */}
          <Box
            component={motion.div}
            initial={{ y: 100, opacity: 0, scale: 0.9 }}
            animate={{
              y: [100, -12, 0],
              opacity: [0, 1, 1],
              scale: [0.9, 1.05, 1],
            }}
            exit={{ y: [0, 8, 100], opacity: [1, 1, 0], scale: [1, 0.95, 0.9] }}
            transition={{ duration: 0.5, times: [0, 0.6, 1], ease: "easeOut" }}
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: { xs: 20, sm: 28 },
              zIndex: 1300,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -6,
                  left: -6,
                  right: -6,
                  bottom: -6,
                  borderRadius: "999px",
                  border: "2px solid rgba(255, 255, 255, 0.4)",
                  pointerEvents: "none",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: -10,
                  left: -10,
                  right: -10,
                  bottom: -10,
                  borderRadius: "999px",
                  border: "1.5px solid rgba(255, 255, 255, 0.2)",
                  pointerEvents: "none",
                },
              }}
            >
              <LoadingButton
                id={id}
                data-testid={dataTestId}
                variant="contained"
                size="large"
                loading={loading}
                onClick={onClick}
                sx={{
                  borderRadius: "999px",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  boxShadow:
                    "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)",
                  backdropFilter: "blur(8px)",
                  "&:hover": {
                    boxShadow:
                      "0 12px 40px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {children}
              </LoadingButton>
            </Box>
          </Box>
        </>
      )}
    </AnimatePresence>
  );
};

export default FloatingButton;
